import { AxiosResponse, AxiosError, AxiosInstance } from 'axios';

import { MUTATIONS as CUSTOMER_MUTATIONS } from '@/store/customer';
import { ApiService } from '../api/api.service';
import {
  IPaymentForm,
  SessionAuth,
  ISessionAuth,
  ConsumerFn,
  TokenizedPaymentCard,
  PaymentProvider,
  InitializeResponse,
  BaseExceptionResponse,
  FormValidationResult,
  PaymentTokenResponse,
  GiftCardBalanceResponse,
} from '@/models';
import { pascalCaseObject, camelCaseObject, PromiseCacher, sleep } from '@/helpers';
import { Config } from '@/environment';
import { Logger } from '../logger';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { AnalyticsService } from '../analytics/analytics.service';
import { PaymentProviders } from '@/constants';

export interface TransactionService {
  savePaymentCard(card: TokenizedPaymentCard): Promise<number>;
  refreshPaymentCards(): Promise<TokenizedPaymentCard[]>;
  deletePaymentCard(tokenId: number): Promise<void>;
  updateLoyaltyAutoAccrualEnrolled(tokenId: number, enrolled: boolean): Promise<void>;
  getGiftCardBalance(giftCardNumber: string): Promise<GiftCardBalanceResponse>;
  enforceMaxLengthOfNumberOnlyInput(event: { key: string; target: { value: string }; preventDefault: () => {} }, maxLength: number): void;
  setGiftCardBalanceCache(giftCardBalance: { [key: string]: number }): void;
  clearGiftCardBalanceCache(): void;
  getProviders(restaurantNumber?: string): Promise<Array<PaymentProvider>>;
  getProvidersByCountry(countryCode: string): Promise<Array<PaymentProvider>>;
  initializeRequest(providerId: string, payload?: object): Promise<InitializeResponse>;
  convertNonceToPaymentToken(providerId: string, clientToken: string, tokenId?: string, restaurantId?: number): Promise<PaymentTokenResponse>;
  validatePaymentForm(paymentForm: IPaymentForm): Promise<FormValidationResult>;
  getSessionToken(providerId?: string): Promise<ISessionAuth>;
  tokenizePayeezyCard(sessionAuth: ISessionAuth, paymentForm: IPaymentForm): Promise<string>;
  getTransactionToken(providerId: string, clientToken: string, tokenId?: string, restaurantId?: number, orderId?: string): Promise<PaymentTokenResponse>;
  getApplePayPaymentSession(merchantIdentifier: string): Promise<any>;
}

export class AppTransactionService extends ApiService implements TransactionService {
  constructor(
    private config: Config,
    private promiseCacher: PromiseCacher,
    protected store: Store<AppState>,
    protected httpClient: AxiosInstance,
    protected logger: Logger,
    protected analytics: AnalyticsService,
  ) {
    super(store, httpClient, logger, analytics);
  }

  async savePaymentCard(card: TokenizedPaymentCard): Promise<number> {
    const accountName = this.config.recaptcha.accountName;
    const cardBody = pascalCaseObject(card);
    const body = accountName ? { ...cardBody, RecaptchaAccount: accountName } : cardBody;
    const tokenId = await this.httpClient
      .post('/transaction/v3/wallet/wallet', body)
      .then((r: AxiosResponse<number>) => r.data)
      .catch((e: AxiosError) => this.logAndThrowApiError('savePaymentCard', e));

    return tokenId;
  }

  async refreshPaymentCards(): Promise<TokenizedPaymentCard[]> {
    const customer = this.store.state.customer.profile;
    if (!customer || customer.isGuest) {
      return Promise.resolve([]);
    }

    const paymentCards = await this.httpClient
      .get('/transaction/v3/wallet/wallet')
      .then((r: AxiosResponse<TokenizedPaymentCard[]>) => r.data.map(token => camelCaseObject(token) as TokenizedPaymentCard))
      .catch(e => this.logAndThrowApiError('refreshPaymentCards', e));

    this.store.commit(CUSTOMER_MUTATIONS.SET_WALLET_PAYMENT_CARDS, paymentCards);

    return paymentCards;
  }

  async deletePaymentCard(tokenId: number): Promise<void> {
    await this.httpClient
      .delete(`/transaction/v3/wallet/wallet/${tokenId}`)
      .then((r: AxiosResponse<any>) => r)
      .catch(e => this.logAndThrowApiError('deletePaymentCard', e));
  }

  async updateLoyaltyAutoAccrualEnrolled(tokenId: number, enrolled: boolean): Promise<void> {
    await this.httpClient
      .patch(`/transaction/v3/wallet/wallet/${tokenId}/LoyaltyAutoAccrualEnrolled`, { optIn: enrolled })
      .then((r: AxiosResponse<any>) => r)
      .catch(e => this.logAndThrowApiError('updateLoyaltyAutoAccrualEnrolled', e));
  }

  async getGiftCardBalance(giftCardNumber: string): Promise<GiftCardBalanceResponse> {
    return this.promiseCacher.exec({
      name: `getGiftCardBalance:${giftCardNumber}`,
      forceRefresh: false,
      inFlightOnly: true,
      workFn: async () => {
        const giftCardBalance = await this.httpClient
          .get(`/transaction/v3/wallet/giftcard/balance/${giftCardNumber}`)
          .then((r: AxiosResponse<GiftCardBalanceResponse>) => r.data)
          .catch(e => this.logAndThrowApiError('getGiftCardBalance', e));

        this.setGiftCardBalanceCache({ ...this.store.state.customer.wallet.giftCardBalances, ...{ [giftCardNumber]: giftCardBalance.currentBalance } });

        return giftCardBalance;
      },
    });
  }

  setGiftCardBalanceCache(giftCardBalance: { [key: string]: number }): void {
    this.store.commit(CUSTOMER_MUTATIONS.SET_WALLET_GIFT_CARD_BALANCES, giftCardBalance);
  }

  clearGiftCardBalanceCache() {
    this.setGiftCardBalanceCache({});
  }

  async getProviders(restaurantNumber = ''): Promise<Array<PaymentProvider>> {
    let response: PaymentProvider[] = [];

    if (!this.store.state.customer.profile) {
      return response;
    }

    try {
      let httpResponse = await this.getProvidersData(restaurantNumber);
      let attemptsLeft = 2;
      while (!this.getProviderSuccess(httpResponse) && attemptsLeft--) {
        httpResponse = await this.getProvidersData(restaurantNumber);
        await sleep(300);
      }
      if (this.getProviderSuccess(httpResponse)) {
        response = httpResponse.data;
      } else {
        throw new Error('getProvider call failed');
      }
    } catch (error: any) {
      this.logger.error(`An error was encountered while fetching payment providers for restaurant number ${restaurantNumber}`, error);
    }

    return response;
  }

  async getProvidersByCountry(countryCode: string): Promise<Array<PaymentProvider>> {
    let response: PaymentProvider[] = [];

    try {
      let httpResponse = await this.getProvidersDataByCountry(countryCode);
      let attemptsLeft = 2;
      while (!this.getProviderSuccess(httpResponse) && attemptsLeft--) {
        httpResponse = await this.getProvidersDataByCountry(countryCode);
        await sleep(300);
      }
      if (this.getProviderSuccess(httpResponse)) {
        response = httpResponse.data;
      } else {
        throw new Error('getProvidersByCountry call failed');
      }
    } catch (error: any) {
      this.logger.error(`An error was encountered while fetching payment providers for country code ${countryCode}`, error);
    }
      
    return response;
  }

  private getProviderSuccess(response: any): boolean {
    return response && (response.status === 200 || response.status === 400);
  }

  async getProvidersData(restaurantNumber = ''): Promise<AxiosResponse<Array<PaymentProvider>>> {
    const provider = await this.httpClient
      .get(`/transaction/v3/wallet/provider/${restaurantNumber}`)
      .then((r: AxiosResponse<Array<PaymentProvider>>) => r)
      .catch((e: AxiosError) => {
        if (!e.response) {
          this.logger.error('Network error making a get provider call', e);
          return Promise.resolve<AxiosResponse<Array<PaymentProvider>>>(<any>null);
        }
        this.logAndThrowApiError('getProviders', e);
      });
    return provider;
  }

  async getProvidersDataByCountry(countryCode: string): Promise<AxiosResponse<Array<PaymentProvider>>> {
    const provider = await this.httpClient
      .get(`/transaction/v3/wallet/provider/country/${countryCode}`)
      .then((r: AxiosResponse<Array<PaymentProvider>>) => r)
      .catch((e: AxiosError) => {
        if (!e.response) {
          this.logger.error('Network error making a get provider call', e);
          return Promise.resolve<AxiosResponse<Array<PaymentProvider>>>(<any>null);
        }
        this.logAndThrowApiError('getProviders', e);
      });
    return provider;
  }

  async initializeRequest(providerId: string, payload?: object | null | undefined): Promise<InitializeResponse> {
    const data = payload ? JSON.stringify(payload) : {};
    const request = await this.httpClient
      .post(`/transaction/v3/wallet/provider/initialize/${providerId}`, data)
      .then((r: AxiosResponse<InitializeResponse>) => {
        return this.processInitializeRequest(r);
      })
      .catch((e: AxiosError) => this.logAndThrowApiError('initializeRequest', e));

    return request;
  }

  async convertNonceToPaymentToken(providerId: string, clientToken: string, tokenId?: string, restaurantId?: number, orderId?: string): Promise<PaymentTokenResponse> {
    const headers = providerId === PaymentProviders.UCom ? { 'tokenid': tokenId } : {};
    let url = `/transaction/v3/wallet/provider/convert/${providerId}/${clientToken}`;
    if(providerId === PaymentProviders.ChaseUK) {
      url += `?source=NOO`;
      if(restaurantId) {
        url += `&restaurantId=${restaurantId}`;
      }
      if(tokenId) {
        url += `&tokenId=${tokenId}`;
      }
      if(orderId) {
        url += `&externalId=COPV2-${orderId}`;
      }
    }
    const token = await this.httpClient
        .get(url, {
        headers: headers
      })
      .then((r: AxiosResponse<PaymentTokenResponse>) => {
        return this.processConvertNonceToPaymentToken(r);
      })
      .catch((e: AxiosError) => {
        this.logAndThrowApiError('convertNonceToPaymentToken', e)
      });

    return token;
  }

  async validatePaymentForm(paymentForm: IPaymentForm): Promise<FormValidationResult> {
    if (!paymentForm) {
      throw new Error('The paymentForm has not been initialized');
    }

    try {
      const result = await new Promise<FormValidationResult>((resolve: any) => {
        let response: FormValidationResult = FormValidationResult.valid();

        try {
          if (paymentForm) {
            paymentForm.validate(() => {
              return;
            });
          }
        } catch (error: any) {
          this.logger.error('Error coming from the validate method: ', error);
          response = FormValidationResult.error(error.message, error.data);
        }
        resolve(response);
      });

      this.store.commit(CUSTOMER_MUTATIONS.SET_CARD_VALIDATION_RESULT, result);
      return result;
    } catch (error: any) {
      const validError = FormValidationResult.error('Your payment could not be processed as requested. Please try again.', []);
      this.store.commit(CUSTOMER_MUTATIONS.SET_CARD_VALIDATION_RESULT, validError);

      this.logger.error('An error occured in validatePaymentForm', error);
      throw new Error('Your payment could not be processed as requested. Please try again.');
    }
  }

  async getSessionToken(providerId: string): Promise<ISessionAuth> {
    let result: ISessionAuth;

    if (!providerId) {
      throw new Error('A valid provider is required');
    }

    try {
      const response = await this.initializeRequest(providerId);
      if (response.clientToken && response.paymentToken) {
        result = new SessionAuth(response.clientToken, response.paymentToken);
      } else {
        throw Error('No request data returned by the service');
      }
    } catch (error: any) {
      this.logger.error('An error was encountered while retrieving a session token from server.', error);
      throw new Error('The request session could not be fulfilled');
    }

    return result;
  }

  async tokenizePayeezyCard(sessionAuth: ISessionAuth, paymentForm: IPaymentForm): Promise<string> {
    if (!sessionAuth) {
      throw new Error('A valid session token is required.');
    }

    if (!paymentForm) {
      throw new Error('The paymentForm has not been initialized');
    }

    try {
      const result = await new Promise<string>(async (resolve: ConsumerFn<string>, reject: ConsumerFn<Error> | undefined) => {
        if (!paymentForm) {
          throw new Error('The paymentForm has not been initialized');
        }

        try {
          paymentForm.submit(sessionAuth, resolve, reject);
        } catch (error: any) {
          throw error;
        }
      });

      return result;
    } catch (error: any) {
      this.logger.error('An error occurred in paymentFormSubmit', error);
      this.logger.error(error.message);
      throw new Error('Your payment could not be processed as requested. Please try again.');
    }
  }

  async getTransactionToken(providerId: string, clientToken: string, tokenId?: string, restaurantId?: number, orderId?: string): Promise<PaymentTokenResponse> {
    if (!providerId) {
      throw new Error('A valid provider is required');
    }
    if (!clientToken) {
      throw new Error('A valid client token is required');
    }

    let response: PaymentTokenResponse = new PaymentTokenResponse();
    try {
      try {
        response = await this.convertNonceToPaymentToken(providerId, clientToken, tokenId, restaurantId, orderId);
      } catch (error: any) {
        this.logger.error('An error was encountered while fetching the token from the server the inner first time', error);
      }

      let attemptsLeft = (providerId !== PaymentProviders.Chase) ? 0 : 4;

      while (!this.paymentTokenIsValid(response) && attemptsLeft--) {
        response = await this.convertNonceToPaymentToken(providerId, clientToken, tokenId, restaurantId, orderId);
        await sleep(1300);
      }
    } catch (error: any) {
      this.logger.error('An error was encountered while fetching the token from the server the first time', error);
    }

    if (this.paymentTokenIsValid(response) && response.paymentToken) {
      return response;
    } else if (response.errorReason) {
      throw new Error(response.errorReason);
    } else {
      throw new Error('The conversion of the token was not successful');
    }
  }

  enforceMaxLengthOfNumberOnlyInput(event: { key: string; target: { value: string }; preventDefault: () => {} }, maxLength: number): void {
    if (!event || !event.target || !event.target.value) {
      return;
    }

    switch (event.key) {
      case 'Backspace':
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'Delete':
      case 'Tab':
      case 'Enter':
        break;
      case '+':
      case '-':
      case '.':
        event.preventDefault();
        break;
      default:
        if (event.target.value.length == maxLength) {
          event.preventDefault();
        }
    }
  }

  async getApplePayPaymentSession(merchantIdentifier: string): Promise<any> {
    try {
      const result =  await this.httpClient.post('/transaction/v3/wallet/wallet/apple/paysession', pascalCaseObject({ domainName: location.host.toLowerCase(), merchantIdentifier })) as AxiosResponse<any>;
      return result?.data;
    } catch (e: any) {
      this.logAndThrowApiError('getApplePayPaymentSession', e);
    }
  }

  private paymentTokenIsValid(response: any): boolean {
    return response && response.isSuccess === true;
  }

  private processInitializeRequest(response: AxiosResponse): Promise<InitializeResponse> {
    const status = response.status;
    if (status === 200) {
      const responseText: any = InitializeResponse.fromJS(response.data);
      return responseText;
    } else if (status === 400) {
      const errorResponse: any = BaseExceptionResponse.fromJS(response.data);
      return errorResponse;
    } else if (status !== 204) {
      return response.data;
    }
    return Promise.resolve<InitializeResponse>(<any>null);
  }

  private processConvertNonceToPaymentToken(response: AxiosResponse): Promise<PaymentTokenResponse> {
    const status = response.status;
    if (status === 200) {
      const responseText: any = PaymentTokenResponse.fromJS(response.data);
      return responseText;
    } else if (status === 400) {
      const errorResponse: any = BaseExceptionResponse.fromJS(response.data);
      return errorResponse;
    } else if (status !== 204) {
      return response.data;
    }
    return Promise.resolve<PaymentTokenResponse>(<any>null);
  }
}
